import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core'
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms'
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs'
import { HotelFormState } from './hotel-comparable-form-state'
import { map, startWith, take, takeUntil } from 'rxjs/operators'
import { mapHotelComparableFormTabs } from './hotel-comparable-form-tabs'
import { createParcelIdentificationFormGroup, ParcelIdentificationFormGroup } from 'src/app/components/parcel-identification/form-group'
import { LandArea, mapAssetClassLandAreaComponentToLandArea, mapLandAreaToAssetClassLandAreaComponent } from 'src/app/core/v3/land-area'
import { CoordinatePoint } from 'src/app/core/v3/coordinate-points'
import { formControlValueChanges } from 'src/app/core/v2/utils'
import moment from 'moment'
import { FileUploadService } from 'src/app/core/file-upload'
import { HttpEventType } from '@angular/common/http'
import { HotelAssetClassService, HotelAssetClassTypeID, HotelComparableAssetClass, HotelTypes, RoomSchedule, RoomScheduleType } from 'src/app/core/v3/asset-class/hotel'
import { normalizeName } from 'src/app/core/v3/utils'
import { ConstructionCategory } from 'src/app/core/v3/asset-class/_sub/construction-category'
import { UpgradesCondition } from 'src/app/core/v3/asset-class/_sub/upgrades-condition'
import { AssetClassSizeModel, AssetClassTenure } from 'src/app/core/comparable'
import { FloorAreaMeasurement } from 'src/app/core/v3/asset-class/_sub/floor-area-measurement'
import { PropertySubTypeService } from 'src/app/core/v2/_services/property-sub-type.service'
import { MeasurementStandardService } from 'src/app/core/v2/_services/measurement-standard.service'
import { Store } from '@ngrx/store'
import { currentUser } from 'src/app/core/mad-auth/mad-auth.store'
import { selectAllUnitAreaMeasurements } from 'src/app/core/linked-tables'
import { AssetClassSize } from 'src/app/core/v2/types'
import { MapLocationComponent } from '../../../shared_components/map-location/map-location.component'
import { AssetClassLandAreaModel } from 'src/app/core/asset_class'
import { LandAreaFormGroup } from 'src/app/components/land-areas/form-group'
import { ServicesAndInfrastructuresComponent } from '../../../shared_components/services-and-infrastructures/services-and-infrastructures.component'
import { ExternalAspectsComponent } from '../../../shared_components/external-aspects/external-aspects.component'
import { InternalAspectsComponent } from '../../../shared_components/internal-aspects/internal-aspects.component'
import { AcGroundsComponent } from '../../../shared_components/ac-grounds/ac-grounds.component'
import { EsgIndicatorsComponent } from '../../../mad_shared/esg-indicators/esg-indicators.component'
import { EsgIndicator } from 'src/app/core/v2/state/esg-indicator/type'
import { ConsiderationAndSourceComponent } from '../../_sub/consideration-and-source/consideration-and-source.component'
import { AcDocumentUploadComponent } from '../../../shared_components/ac-document-upload/ac-document-upload.component'
import { UploadFileComponent } from '../../../shared_components/upload-file/upload-file.component'
import { v4 as uuidv4 } from 'uuid'
import { ComparableLocationComponent } from 'src/app/components/location/comparable-location/comparable-location.component'

interface Option {
    id: number,
    name: string
}

interface UploadFile {
    isUploaded: boolean,
    file: {
        name: string | null,
        display_name: string | null,
        path: string | null
    } | null
}

@Component({
    selector: 'app-hotel-comparable-form',
    templateUrl: './hotel-comparable-form.component.html',
    styleUrls: ['./hotel-comparable-form.component.scss']
})
export class HotelComparableFormComponent implements OnDestroy {
    /** ViewChilds */
    @ViewChild(ComparableLocationComponent, { static: false })
    locationComponent!: ComparableLocationComponent

    @ViewChild('hotelAccommodationFileInput')
    hotelAccommodationFileInputRef!: ElementRef<HTMLInputElement>

    @ViewChild('roomScheduleFileInput')
    roomScheduleFileInputRef!: ElementRef<HTMLInputElement>

    @ViewChild('outletsFileInput')
    outletsFileInputRef!: ElementRef<HTMLInputElement>

    @ViewChild(ServicesAndInfrastructuresComponent, { static: false })
    servicesAndInfraComponent!: ServicesAndInfrastructuresComponent

    @ViewChild(ExternalAspectsComponent, { static: false })
    externalAspectComponent!: ExternalAspectsComponent

    @ViewChild(InternalAspectsComponent, { static: false })
    internalAspectComponent!: InternalAspectsComponent

    @ViewChild(AcGroundsComponent, { static: true })
    acGroundsComponent!: AcGroundsComponent

    @ViewChild(EsgIndicatorsComponent, { static: false })
    esgIndicatorComponent!: EsgIndicatorsComponent

    @ViewChild(ConsiderationAndSourceComponent, { static: false })
    considerationAndSourceComponent!: ConsiderationAndSourceComponent;

    @ViewChild('simple')
    public uploadFileComponent!: UploadFileComponent;

    @ViewChild(AcDocumentUploadComponent, { static: false })
    acDocumentUploadComponent!: AcDocumentUploadComponent

    /** Const */
    HotelTypes = HotelTypes
    HotelAssetClassTypeID = HotelAssetClassTypeID
    HotelAssetClassTypeIDStr = HotelAssetClassTypeID.toString()

    /** State */
    private _markerInfo$ = new BehaviorSubject<{
        lat: number | null,
        lng: number | null,
        isShow: boolean
    }>({
        lat: null,
        lng: null,
        isShow: false
    })
    private _points$ = new BehaviorSubject<CoordinatePoint[]>([])
    private _landAreas$ = new BehaviorSubject<LandArea[]>([])
    private _formType$ = new BehaviorSubject<'simple' | 'complex'>('simple')
    private _hotelTypes$ = new BehaviorSubject<Option[]>([])
    private _hotelRatings$ = new BehaviorSubject<Option[]>([])
    private _hotelApartmentRatings$ = new BehaviorSubject<Option[]>([])
    private _constructionCategoryIds: (number | null)[] = []
    private _upgradesConditionIds: (number | null)[] = []
    private _roomScheduleIds: (number | null)[] = []
    private _floorAreaMeasurementIds: (number | null)[] = []
    private _assetClass$ = new BehaviorSubject<HotelComparableAssetClass>(new HotelComparableAssetClass())

    private _hotelAccommodationFile$ = new BehaviorSubject<UploadFile>({ isUploaded: false, file: null })
    private _roomScheduleFile$ = new BehaviorSubject<UploadFile>({ isUploaded: false, file: null })
    private _outletsFile$ = new BehaviorSubject<UploadFile>({ isUploaded: false, file: null })

    private _totalRoomSchedule$ = new BehaviorSubject<number>(0)
    private _propertySubTypes$ = new BehaviorSubject<{ id: number, name: string }[]>([])
    private _measurementStandards$ = new BehaviorSubject<{ id: number, name: string, property_sub_type_id: number }[]>([])
    private _currentUser$ = this._ngStore.select(currentUser)
    private _roomScheduleType$ = new BehaviorSubject<RoomScheduleType>('upload')
    private _roomSchedules$ = new BehaviorSubject<RoomSchedule[]>([])
    propertySubTypes$ = this._propertySubTypes$.asObservable()
    measurementStandards$ = this._measurementStandards$.asObservable()
    centerLat: number | null = null
    centerLng: number | null = null
    landAreas$ = this._landAreas$.asObservable()
    coordinatePoints$ = this._points$.asObservable()
    markerInfo$ = this._markerInfo$.asObservable()
    isComplexForm$ = this._formType$.asObservable().pipe(
        map(formType => formType === 'complex')
    )
    hotelTypes$ = this._hotelTypes$.asObservable()
    hotelRatings$ = this._hotelRatings$.asObservable()
    hotelApartmentRatings$ = this._hotelApartmentRatings$.asObservable()
    hotelAccommodationFile$ = this._hotelAccommodationFile$.asObservable()
    roomScheduleFile$ = this._roomScheduleFile$.asObservable()
    outletsFile$ = this._outletsFile$.asObservable()
    totalRoomSchedule$ = this._totalRoomSchedule$.asObservable()
    assetClass$ = this._assetClass$.asObservable()
    locationData$ = this.assetClass$.pipe(
        map(assetClass => assetClass.location_data)
    )
    serviceAndInfraData$ = this.assetClass$.pipe(
        map(assetClass => assetClass.service_and_infra)
    )
    externalAspectData$ = this.assetClass$.pipe(
        map(assetClass => assetClass.external_aspect)
    )
    internalAspectData$ = this.assetClass$.pipe(
        map(assetClass => assetClass.internal_aspect)
    )
    groundsData$ = this.assetClass$.pipe(
        map(assetClass => assetClass.grounds)
    )
    sizesSubject$ = new BehaviorSubject<AssetClassSizeModel[]>([])
    esgIndicatorData$ = this.assetClass$.pipe(
        map(assetClass => assetClass.esg_indicator)
    )
    userUnitOfMeasurementId$ = this._currentUser$.pipe(
        map(user => {
            if (!user) return null;
            return user.settings.unit_of_measurement_id
        })
    )
    tenuresSubject = new BehaviorSubject<AssetClassTenure[]>([])
    comps = new BehaviorSubject<any>([]);
    hasParcel = of(false);
    timezoneOffset: string | null = null
    sizes$ = combineLatest([
        this._ngStore.select(selectAllUnitAreaMeasurements),
        this.sizesSubject$.asObservable()
    ]).pipe(
        map(([unitAreaMeasurements, sizes]) => {
            const items: AssetClassSize[] = sizes.map(size => ({
                id: size.id,
                uid: size.uid,
                size: size.size,
                standardMeasurementName: size.standard_measurement_name,
                unitAreaMeasurementAcronym: unitAreaMeasurements.find(item => item.id === size.unit_of_area_measurement_id)?.acronym ?? ''
            }))
            return items
        })
    )
    considerationError$ = new BehaviorSubject<boolean | undefined>(undefined);
    acPictures$ = this.assetClass$.pipe(
        map(assetClass => assetClass.ac_pictures)
    )
    picture$ = this.assetClass$.pipe(
        map(assetClass => assetClass.picture)
    )
    documents$ = this.assetClass$.pipe(
        map(assetClass => assetClass.documents)
    )
    isSavedBefore = false
    roomScheduleType$ = this._roomScheduleType$.asObservable()
    roomSchedules$ = this._roomSchedules$.asObservable()

    /** Form */
    private _formState$ = new BehaviorSubject<HotelFormState>({})
    form = new FormGroup({
        parcel_identification: createParcelIdentificationFormGroup({
            addInfoOnLand: false,
            boundaryType: 1,
            coordinateReferenceSystemId: null,
            landArea: {
                envConsiderations: null,
                otherConsiderations: null,
                planningStatusId: null,
                qosDegradationId: null,
                plotArea: null
            },
            landParcelName: null
        }),

        property_type_id: new FormControl<number | null>(null, [Validators.required]),
        hotel_rating_id: new FormControl<number | null>(null),
        hotel_apartment_rating_id: new FormControl<number | null>(null),
        completion_status: new FormControl<string | null>(null),
        completion_date: new FormControl<Date | null>(null),
        estimated_age: new FormControl<number | null>(null),
        estimated_remaining_lifespan_from: new FormControl<number | null>(null),
        estimated_remaining_lifespan_to: new FormControl<number | null>(null),
        developer: new FormControl<string | null>(null),
        number_of_stories: new FormControl<string | null>(null),
        views: new FormControl<string | null>(null),
        plot_id: new FormControl<string | null>(null),
        plot_area: new FormControl<number | null>(null),
        makani_no: new FormControl<string | null>(null),
        dewa_no: new FormControl<string | null>(null),
        municipality_no: new FormControl<string | null>(null),
        state_of_repair_desc: new FormControl<string | null>(null),
        construction_desc: new FormControl<string | null>(null),
        construction_category_form_array: new FormArray<FormGroup<{
            title: FormControl<string | null>,
            description: FormControl<string | null>
        }>>([]),
        upgrades_condition_id: new FormControl<number | null>(null),
        upgrades_conditions_form_array: new FormArray<FormControl<string | null>>([]),
        room_schedule_type: new FormControl<RoomScheduleType>('upload'),
        number_of_units: new FormControl<string | null>(null),
        room_schedule_form_array: new FormArray<FormGroup<{
            room_type: FormControl<string | null>,
            number: FormControl<number | null>
        }>>([]),
        floor_area_measurement_comment: new FormControl<string | null>(null),
        floor_area_measurement_form_array: new FormArray<FormGroup<{
            title: FormControl<string | null>,
            property_sub_type_id: FormControl<number | null>,
            measurement_standard_id: FormControl<number | null>,
            name: FormControl<string | null>,
            path: FormControl<string | null>,
            display_name: FormControl<string | null>
        }>>([])
    })
    hasFormErrors = false

    /** Tabs */
    selectedTab = 0
    formTabHeaders$ = this._formState$.asObservable().pipe(
        map(state => mapHotelComparableFormTabs(state))
    )

    /** Input */
    @Input()
    set formType(value: 'simple' | 'complex') {
        this._formType$.next(value)
    }

    @Input()
    set assetClass(value: HotelComparableAssetClass | null) {
        if (value === null || value === undefined) {
            this._setConstructionCategoryFormArray([])
            this._setRoomScheduleFormArray([])
            this._setFloorAreaMeasurementFormArrray([])
            return;
        }
        this._assetClass$.next(value)
        this.isSavedBefore = true;
        this._markerInfo$.next({
            lat: value.location_data.latitude,
            lng: value.location_data.longitude,
            isShow: true
        })
        this._points$.next(value.points.map(item => ({ latitude: Number(item.lat), longitude: Number(item.lng) })))
        this._landAreas$.next(value.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))

        const deepCopiedSizes = _deepCopy(value.sizes)
            .map(size => {
                size.uid = uuidv4()
                return size
            })
        const deepCopiedTenures = _deepCopy(value.tenures)
            .map(tenure => {
                const rentSizeUuid = deepCopiedSizes.find(size => size.id === tenure.rent_size_id)?.uid
                const expensesSizeUuid = deepCopiedSizes.find(size => size.id === tenure.expenses_size_id)?.uid
                tenure.rent_size_uid = rentSizeUuid
                tenure.expenses_size_uid = expensesSizeUuid
                return tenure
            })

        this.tenuresSubject.next(deepCopiedTenures)
        this.sizesSubject$.next(deepCopiedSizes);

        this.form.patchValue({
            property_type_id: value.property_type_id,
            hotel_rating_id: value.hotel_rating_id,
            hotel_apartment_rating_id: value.hotel_apartment_rating_id,
            completion_status: value.completion_status,
            estimated_age: value.estimated_age,
            estimated_remaining_lifespan_from: value.estimated_remaining_lifespan_from,
            estimated_remaining_lifespan_to: value.estimated_remaining_lifespan_to,
            developer: value.developer,
            number_of_stories: value.number_of_stories,
            views: value.views,
            plot_id: value.plot_id,
            plot_area: value.plot_area,
            makani_no: value.makani_no,
            dewa_no: value.dewa_no,
            municipality_no: value.municipality_no,
            state_of_repair_desc: value.state_of_repair_desc,
            construction_desc: value.construction_desc,
            upgrades_condition_id: value.upgrades_condition_id,
            number_of_units: value.number_of_units,
            floor_area_measurement_comment: value.floor_area_measurement_comment,
            room_schedule_type: value.room_schedule_type
        })

        this._setConstructionCategoryFormArray(value.construction_categories)
        this._setUpgradesConditionFormArray(value.upgrades_conditions)
        this._setRoomScheduleFormArray(value.room_schedules)
        this._setFloorAreaMeasurementFormArrray(value.floor_area_measurements)

        if (value.hotel_accommodation_file) {
            this._hotelAccommodationFile$.next({
                isUploaded: true,
                file: {
                    name: value.hotel_accommodation_file.name,
                    path: value.hotel_accommodation_file.path,
                    display_name: normalizeName(value.hotel_accommodation_file.name)
                }
            })
        }

        if (value.room_schedule_file) {
            this._roomScheduleFile$.next({
                isUploaded: true,
                file: {
                    name: value.room_schedule_file.name,
                    path: value.room_schedule_file.path,
                    display_name: normalizeName(value.room_schedule_file.name)
                }
            })
        }

        if (value.outlet_file) {
            this._outletsFile$.next({
                isUploaded: true,
                file: {
                    name: value.outlet_file.name,
                    path: value.outlet_file.path,
                    display_name: normalizeName(value.outlet_file.name)
                }
            })
        }
    }

    /** Misc */
    private _onDestroy$ = new Subject<void>()


    constructor(
        private _hotelAssetClassService: HotelAssetClassService,
        private _fileUploadService: FileUploadService,
        private _cdr: ChangeDetectorRef,
        private _propertySubTypeService: PropertySubTypeService,
        private _measurementStandardService: MeasurementStandardService,
        private _ngStore: Store
    ) {
        this._formEffects()

        this._fetchHotelTypes()
        this._fetchHotelRatings()
        this._fetchHotelApartmentRatings()
        this._fetchPropertySubTypes()
        this._fetchMeasurementStandards()
    }

    ngOnDestroy(): void {
        this._onDestroy$.next()
        this._onDestroy$.complete()
    }

    /** Tabs */
    onTabChange(index: number) {
        this.selectedTab = index
    }

    /** Map */
    onMapLocationFormChange(event: boolean | undefined) {
        const state = this._formState$.getValue()
        this._formState$.next({
            ...state,
            map_location: {
                invalid: event,
                requiredFields: 0
            }
        })
    }

    onMarkerChange(event: { lat: number, lng: number, isShown: boolean }) {
        if (event == null) {
            return;
        }
        this._markerInfo$.next({
            lat: event.lat,
            lng: event.lng,
            isShow: event.isShown
        })
    }

    /** Parcel Identification */
    onCoordinatePointsChange(points: CoordinatePoint[]) {
        this._points$.next(points)
    }

    onLandAreasChange(items: LandArea[]) {
        this._landAreas$.next(items)
    }

    /** About Property */
    onClearCompletionDate() {
        this.form.controls.completion_date.patchValue(null)
        this.form.controls.completion_date.updateValueAndValidity()
    }

    onAddConstructionCategory() {
        this._addConstructionCategory({ id: null, title: null, description: null })
    }

    onRemoveConstructionCategory(index: number) {
        this.form.controls.construction_category_form_array.removeAt(index)
    }

    onAddUpgradesAndSpecialCondition() {
        this._addUpgradesCondition({ id: null, value: null })
    }

    onRemoveUpgradesAndSpecialCondition(index: number) {
        this.form.controls.upgrades_conditions_form_array.removeAt(index)
    }

    onClickUploadHotelAccommodation() {
        this.hotelAccommodationFileInputRef.nativeElement.click()
    }

    onUploadHotelAccommodationFile(event: any) {
        const files: FileList = event.target.files
        const formData = new FormData()
        Array.from(files).forEach((file, i) => {
            formData.append('files[' + i + ']', file, file.name)
        })
        event.srcElement.value = null

        this._fileUploadService.uploadFile(formData).subscribe(value => {
            if (value.type !== HttpEventType.Response) {
                return;
            }
            const body = value.body as any
            this._hotelAccommodationFile$.next({
                isUploaded: true,
                file: {
                    name: body.uploaded_files[0].name,
                    path: body.uploaded_files[0].path,
                    display_name: normalizeName(body.uploaded_files[0].name)
                }
            })
        })
    }

    onClickUploadRoomSchedule() {
        this.roomScheduleFileInputRef.nativeElement.click()
    }

    onUploadRoomScheduleFile(event: any) {
        const files: FileList = event.target.files
        const formData = new FormData()
        Array.from(files).forEach((file, i) => {
            formData.append('files[' + i + ']', file, file.name)
        })
        event.srcElement.value = null

        this._fileUploadService.uploadFile(formData).subscribe(value => {
            if (value.type !== HttpEventType.Response) {
                return;
            }
            const body = value.body as any
            this._roomScheduleFile$.next({
                isUploaded: true,
                file: {
                    name: body.uploaded_files[0].name,
                    path: body.uploaded_files[0].path,
                    display_name: normalizeName(body.uploaded_files[0].name)
                }
            })
        })
    }

    onClickUploadOutlets() {
        this.outletsFileInputRef.nativeElement.click()
    }

    onUploadOutletsFile(event: any) {
        const files: FileList = event.target.files
        const formData = new FormData()
        Array.from(files).forEach((file, i) => {
            formData.append('files[' + i + ']', file, file.name)
        })
        event.srcElement.value = null

        this._fileUploadService.uploadFile(formData).subscribe(value => {
            if (value.type !== HttpEventType.Response) {
                return;
            }
            const body = value.body as any
            this._outletsFile$.next({
                isUploaded: true,
                file: {
                    name: body.uploaded_files[0].name,
                    path: body.uploaded_files[0].path,
                    display_name: normalizeName(body.uploaded_files[0].name)
                }
            })
        })
    }

    onAddRoomSchedule() {
        this._addRoomSchedule({ id: null, room_type: null, number: null })
    }

    onRemoveRoomSchedule(index: number) {
        this.form.controls.room_schedule_form_array.removeAt(index)
    }

    onClickUploadFloorAreaMeasurement(index: number) {
        const inputElement = document.createElement('input');
        inputElement.type = 'file'
        inputElement.accept = '.csv'
        inputElement.multiple = false
        inputElement.click()

        inputElement.onchange = () => {
            this._uploadFloorAreaMeasurement(inputElement.files, index)
        }
    }

    onAddFloorAreaMeasurement() {
        this._addFloorAreaMeasurement({
            id: null,
            title: null,
            name: null,
            path: null,
            measurement_standard_id: null,
            property_sub_type_id: null
        })
    }
    onRemoveFloorAreaMeasurement(index: number) {
        this.form.controls.floor_area_measurement_form_array.removeAt(index)
    }

    filterMeasurementStandards(index: number, standards: { id: number, name: string, property_sub_type_id: number }[]) {
        const group = this.form.controls.floor_area_measurement_form_array.at(index)
        return standards.filter(item => item.property_sub_type_id === group.controls.property_sub_type_id.value || !item.property_sub_type_id)
    }

    onCenterChange(event: CoordinatePoint) {
        this.centerLat = event.latitude
        this.centerLng = event.longitude
    }

    onMarkerChangeV2(event: CoordinatePoint) {
        if (event == null) {
            return;
        }
        this._markerInfo$.next({
            lat: event.latitude,
            lng: event.longitude,
            isShow: true
        })
    }

    private _setFloorAreaMeasurementFormArrray(items: FloorAreaMeasurement[]) {
        this.form.controls.floor_area_measurement_form_array.clear()
        this._floorAreaMeasurementIds = []
        const _items = items.length === 0
            ? [{ id: null, title: null, name: null, path: null, property_sub_type_id: null, measurement_standard_id: null }]
            : [...items]
        _items.forEach(item => {
            this._addFloorAreaMeasurement(item)
        })
    }

    private _addFloorAreaMeasurement(item: FloorAreaMeasurement) {
        this.form.controls.floor_area_measurement_form_array.push(new FormGroup({
            title: new FormControl<string | null>(item.title),
            name: new FormControl<string | null>(item.name),
            path: new FormControl<string | null>(item.path),
            display_name: new FormControl<string | null>(normalizeName(item.name)),
            property_sub_type_id: new FormControl<number | null>(item.property_sub_type_id),
            measurement_standard_id: new FormControl<number | null>(item.measurement_standard_id)
        }))
        this._floorAreaMeasurementIds.push(item.id)
    }

    private _uploadFloorAreaMeasurement(files: FileList | null, index: number) {
        if (files === null) return;
        const formData = new FormData()
        Array.from(files).forEach((file, i) => {
            formData.append('files[' + i + ']', file, file.name)
        })

        this._fileUploadService.uploadFile(formData).subscribe(value => {
            if (value.type !== HttpEventType.Response) {
                return;
            }
            const body = value.body as any
            const group = this.form.controls.floor_area_measurement_form_array.at(index)
            group.controls.name.patchValue(body.uploaded_files[0].name)
            group.controls.path.patchValue(body.uploaded_files[0].path)
            group.controls.display_name.patchValue(normalizeName(body.uploaded_files[0].name))
            this._cdr.markForCheck()
        })
    }

    private _getConstructionCategoryValues(): ConstructionCategory[] {
        const values: ConstructionCategory[] = []
        this.form.controls.construction_category_form_array.controls.forEach((group, i) => {
            const _new: ConstructionCategory = { id: null, title: null, description: null }
            if (this._constructionCategoryIds[i]) {
                _new.id = this._constructionCategoryIds[i]
            }
            _new.title = group.controls.title.value
            _new.description = group.controls.description.value
            values.push(_new)
        })
        return values
    }

    private _setConstructionCategoryFormArray(items: ConstructionCategory[]) {
        this.form.controls.construction_category_form_array.clear()
        this._constructionCategoryIds = []
        const _items = items.length === 0
            ? [{ id: null, title: null, description: null }]
            : [...items]
        _items.forEach(item => {
            this._addConstructionCategory(item)
        })
    }

    private _addConstructionCategory(item: ConstructionCategory) {
        this.form.controls.construction_category_form_array.push(new FormGroup({
            title: new FormControl<string | null>(item.title),
            description: new FormControl<string | null>(item.description)
        }))
        this._constructionCategoryIds.push(item.id)
    }

    private _setUpgradesConditionFormArray(items: UpgradesCondition[]) {
        this.form.controls.upgrades_conditions_form_array.clear()
        this._upgradesConditionIds = []
        items.forEach(item => this._addUpgradesCondition(item))
    }

    private _getUpgradesConditionValues(): UpgradesCondition[] {
        const values: UpgradesCondition[] = []
        this.form.controls.upgrades_conditions_form_array.controls.forEach((control, i) => {
            const _new: UpgradesCondition = { id: null, value: null }
            if (this._upgradesConditionIds[i]) {
                _new.id = this._upgradesConditionIds[i]
            }
            _new.value = control.value
            values.push(_new)
        })
        return values
    }

    private _getRoomScheduleValues(): RoomSchedule[] {
        const values: RoomSchedule[] = []
        this.form.controls.room_schedule_form_array.controls.forEach((group, i) => {
            const _new: RoomSchedule = { id: null, number: null, room_type: null }
            if (this._roomScheduleIds[i]) {
                _new.id = this._roomScheduleIds[i]
            }
            _new.number = group.controls.number.value
            _new.room_type = group.controls.room_type.value
            values.push(_new)
        })
        return values
    }

    private _getFloorAreaMeasurementValues(): FloorAreaMeasurement[] {
        const vals: FloorAreaMeasurement[] = []
        this.form.controls.floor_area_measurement_form_array.controls.forEach((group, i) => {
            const _new: FloorAreaMeasurement = { id: null, title: null, property_sub_type_id: null, measurement_standard_id: null, name: null, path: null }
            if (this._floorAreaMeasurementIds[i]) {
                _new.id = this._floorAreaMeasurementIds[i]
            }
            _new.title = group.controls.title.value
            _new.property_sub_type_id = group.controls.property_sub_type_id.value
            _new.measurement_standard_id = group.controls.measurement_standard_id.value
            _new.name = group.controls.name.value
            _new.path = group.controls.path.value
            vals.push(_new)
        })
        return vals;
    }

    private _addUpgradesCondition(item: UpgradesCondition) {
        this.form.controls.upgrades_conditions_form_array.push(new FormControl<string | null>(item.value, [Validators.required]))
        this._upgradesConditionIds.push(item.id)
    }

    private _setRoomScheduleFormArray(items: RoomSchedule[]) {
        this.form.controls.room_schedule_form_array.clear()
        this._roomScheduleIds = []
        const _items = items.length === 0
            ? [{ id: null, room_type: null, number: null }]
            : [...items]
        _items.forEach(item => this._addRoomSchedule(item))
    }

    private _addRoomSchedule(item: RoomSchedule) {
        this.form.controls.room_schedule_form_array.push(new FormGroup({
            room_type: new FormControl<string | null>(item.room_type),
            number: new FormControl<number | null>(item.number)
        }))
        this._roomScheduleIds.push(item.id)
    }

    /** Considerations */
    onConsiderationsChange(considerations: AssetClassTenure[]) {
        if (considerations.length > 0) {
            this.considerationError$.next(false)
        } else {
            this.considerationError$.next(undefined)
        }
    }

    /** Submit */
    submit(isComplete: boolean) {

        let esgIndicatorValues: EsgIndicator | null = null
        if (this.esgIndicatorComponent) {
            const esgIndicatorResult = this.esgIndicatorComponent.submit(!isComplete)
            if (esgIndicatorResult.type === 'success') {
                esgIndicatorValues = esgIndicatorResult.values
            } else if (esgIndicatorResult.type === 'error') {
                this.hasFormErrors = true
            }
        }

        if (this.hasFormErrors) {
            return;
        }

        return this._prepareAssetClass(isComplete, esgIndicatorValues)
    }

    private _prepareAssetClass(isComplete: boolean, esgIndicator: EsgIndicator | null): { ac: HotelComparableAssetClass, fileList: any[] } {
        const assetClass = this._assetClass$.getValue()
        const ac = new HotelComparableAssetClass();

        ac.id = assetClass.id
        ac.status = isComplete ? 1 : 0;
        ac.form_type = this._formType$.getValue() === 'complex' ? 1 : 2
        ac.esg_indicator = esgIndicator

        // Map Location
        if (this.locationComponent) {
            ac.location_data = this.locationComponent.getData()
        }
        const parcelIdentificationForm = this.form.controls.parcel_identification as ParcelIdentificationFormGroup
        const landAreaForm = parcelIdentificationForm.controls.land_area as LandAreaFormGroup
        const landArea = new AssetClassLandAreaModel()
        landArea.clear()
        landArea.id = assetClass.landArea.id
        landArea.planning_status_id = landAreaForm.controls.planning_status_id.value
        landArea.qos_degradation_id = landAreaForm.controls.qos_degradation_id.value
        landArea.env_considerations = landAreaForm.controls.env_considerations.value
        landArea.other_considerations = landAreaForm.controls.other_considerations.value
        ac.add_info_on_land = parcelIdentificationForm.controls.add_info_on_land.value;
        ac.land_parcel_name = parcelIdentificationForm.controls.land_parcel_name.value;
        ac.coordinate_reference_system_id = parcelIdentificationForm.controls.coordinate_reference_system_id.value;
        ac.boundary_type_id = parcelIdentificationForm.controls.boundary_type.value
        ac.points = this._points$.value.map(item => ({ lat: item.latitude, lng: item.longitude }));
        ac.landArea = landArea;
        ac.landAreaComponents = this._landAreas$.value.map(item => mapLandAreaToAssetClassLandAreaComponent(item))

        // About Property
        ac.property_type_id = this.form.controls.property_type_id.value
        ac.hotel_rating_id = this.form.controls.hotel_rating_id.value
        ac.hotel_apartment_rating_id = this.form.controls.hotel_apartment_rating_id.value
        ac.completion_status = this.form.controls.completion_status.value
        let completionDate = this.form.controls.completion_date.value
        if (completionDate && moment.isMoment(completionDate)) {
            completionDate = completionDate.toDate()
        }
        ac.completion_date = completionDate
        ac.estimated_age = this.form.controls.estimated_age.value
        ac.estimated_remaining_lifespan_from = this.form.controls.estimated_remaining_lifespan_from.value
        ac.estimated_remaining_lifespan_to = this.form.controls.estimated_remaining_lifespan_to.value
        ac.developer = this.form.controls.developer.value
        ac.number_of_stories = this.form.controls.number_of_stories.value
        ac.views = this.form.controls.views.value
        ac.makani_no = this.form.controls.makani_no.value
        ac.dewa_no = this.form.controls.dewa_no.value
        ac.municipality_no = this.form.controls.municipality_no.value
        ac.state_of_repair_desc = this.form.controls.state_of_repair_desc.value
        ac.construction_desc = this.form.controls.construction_desc.value
        ac.construction_categories = this._getConstructionCategoryValues()
        ac.upgrades_condition_id = this.form.controls.upgrades_condition_id.value
        ac.upgrades_conditions = this._getUpgradesConditionValues()
        ac.room_schedule_type = this.form.controls.room_schedule_type.value
        ac.number_of_units = this.form.controls.number_of_units.value
        ac.room_schedules = this._getRoomScheduleValues()
        ac.floor_area_measurement_comment = this.form.controls.floor_area_measurement_comment.value
        ac.floor_area_measurements = this._getFloorAreaMeasurementValues()
        ac.plot_id = this.form.controls.plot_id.value
        ac.plot_area = this.form.controls.plot_area.value
        const hotelAccommodationFile = this._hotelAccommodationFile$.getValue()
        ac.hotel_accommodation_file = hotelAccommodationFile.isUploaded
            ? { name: hotelAccommodationFile.file?.name ?? null, path: hotelAccommodationFile.file?.path ?? null }
            : null
        const roomScheduleFile = this._roomScheduleFile$.getValue()
        ac.room_schedule_file = roomScheduleFile.isUploaded
            ? { name: roomScheduleFile.file?.name ?? null, path: roomScheduleFile.file?.path ?? null }
            : null
        const outletFile = this._outletsFile$.getValue()
        ac.outlet_file = outletFile.isUploaded
            ? { name: outletFile.file?.name ?? null, path: outletFile.file?.path ?? null }
            : null

        if (this.servicesAndInfraComponent) {
            ac.service_and_infra = this.servicesAndInfraComponent.getdata();
        }

        if (this.externalAspectComponent) {
            ac.external_aspect = this.externalAspectComponent.getData();
        }

        if (this.internalAspectComponent) {
            ac.internal_aspect = this.internalAspectComponent.getData();
        }

        if (this.acGroundsComponent) {
            ac.grounds = { ...this.acGroundsComponent.getData(), id: assetClass.grounds.id };
        }

        this.uploadFileComponent.res$.pipe(take(1)).subscribe(value => {
            if (value) {
                ac.picture = value.success;
            }
        })
        ac.sizes = this.sizesSubject$.value;
        ac.tenures = this.considerationAndSourceComponent ? this.considerationAndSourceComponent.getData() : [];
        if (this.acDocumentUploadComponent) {
            ac.documents = this.acDocumentUploadComponent.getDocuments();
        }

        return {
            ac,
            fileList: this.uploadFileComponent ? this.uploadFileComponent.uploadFiles : []
        };
    }

    /** Form Effects */
    private _formEffects() {
        formControlValueChanges(this.form.controls.property_type_id).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(value => {
            if (value === HotelTypes.Hotel) {
                this.form.controls.hotel_rating_id.setValidators(Validators.required)
            } else {
                this.form.controls.hotel_rating_id.removeValidators(Validators.required)
            }

            if (value === HotelTypes.HotelApartments) {
                this.form.controls.hotel_apartment_rating_id.setValidators(Validators.required)
            } else {
                this.form.controls.hotel_apartment_rating_id.removeValidators(Validators.required)
            }

            this.form.controls.hotel_rating_id.updateValueAndValidity({ emitEvent: false })
            this.form.controls.hotel_apartment_rating_id.updateValueAndValidity({ emitEvent: false })
        })

        formControlValueChanges(this.form.controls.room_schedule_type).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(value => {
            if (value === 'upload') {
                this.form.controls.number_of_units.setValidators(Validators.required)
            } else {
                this.form.controls.number_of_units.removeValidators(Validators.required)
            }

            this.form.controls.number_of_units.updateValueAndValidity({ emitEvent: false })

            this._roomScheduleType$.next(value ?? 'upload')
        })

        this.form.controls.room_schedule_form_array.valueChanges.pipe(
            startWith(this.form.controls.room_schedule_form_array.value),
            takeUntil(this._onDestroy$)
        ).subscribe(values => {
            // Total
            const total = values.reduce(
                (acc, value) => {
                    if (value.number) {
                        return acc + value.number
                    }
                    return acc
                },
                0
            )
            this._totalRoomSchedule$.next(total)

            // Schedules
            const _values: RoomSchedule[] = []
            values.forEach((value, i) => {
                const _new: RoomSchedule = { id: null, room_type: null, number: null }
                if (this._roomScheduleIds[i]) {
                    _new.id = this._roomScheduleIds[i]
                }
                _new.room_type = value.room_type ?? null
                _new.number = value.number ?? null
                _values.push(_new)
            })
            this._roomSchedules$.next(_values)
        })
    }

    /** Fetch Effects */
    private _fetchHotelTypes() {
        this._hotelAssetClassService.fetchHotelTypes().pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(value => {
            this._hotelTypes$.next(value)
        })
    }

    private _fetchHotelRatings() {
        this._hotelAssetClassService.fetchHotelRatings().pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(value => {
            this._hotelRatings$.next(value)
        })
    }

    private _fetchHotelApartmentRatings() {
        this._hotelAssetClassService.fetchHotelApartmentRatings().pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(value => {
            this._hotelApartmentRatings$.next(value)
        })
    }

    private _fetchPropertySubTypes() {
        this._propertySubTypeService.fetchListing().subscribe(response => {
            this._propertySubTypes$.next(response)
        })
    }
    private _fetchMeasurementStandards() {
        this._measurementStandardService.fetchListing().subscribe(response => {
            this._measurementStandards$.next(response)
        })
    }
}

function _deepCopy(arr: any[]): any[] {
    return arr.map(item => ({ ...item }));
}